import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useScreen } from "@danishagro/shared/src/contexts/screen.context";
import { GoogleMap, useLoadScript, InfoWindow, Marker } from "@react-google-maps/api";
import { DA_IconNames } from "@danishagro/shared/src/components/atoms/Icon/Icon.component";
import { DA_Title } from "@danishagro/shared/src/components/atoms/Title/Title.component";
import { DA_InlineLink } from "@danishagro/shared/src/components/atoms/InlineLink/InlineLink.component";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import { DA_Spinner } from "@danishagro/shared/src/components/atoms/Spinner/Spinner.component";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import { DepartmentProps } from "../Department/Department.interface";
import { MapProps } from "./Map.interface";
import { usePinIcon } from "./hooks/usePinIcon.hook";
import S from "./Map.module.scss";

const Map = (props: MapProps) => {
    const googleMapsKey = globalThis.siteSettingsObj?.googleMapsKey;

    const { siteVariant } = useAppData();
    const { mapPinSrc } = usePinIcon(siteVariant.alias);

    const { isLoaded } = useLoadScript({
        id: "google-map-script",
        googleMapsApiKey: googleMapsKey,
    });

    const { getDictionaryString } = useTranslations();
    const { isMobile } = useScreen();

    const mapRef = useRef<google.maps.Map | null>(null);

    useEffect(() => {
        if (mapRef.current && props.activeMarker?.props?.googleMap?.address?.coordinates) {
            const { lat, lng } = props.activeMarker.props.googleMap.address.coordinates;
            mapRef.current.panTo({ lat, lng });
        }
    }, [props.activeMarker]);

    const DEFAULT_CENTER_COORDINATES = {
        lat: 56.04,
        lng: 11.57,
    };

    const CENTER_COORDINATES =
        typeof props?.centerLat === "number" &&
        typeof props?.centerLng === "number" &&
        isFinite(props.centerLat) &&
        isFinite(props.centerLng)
            ? { lat: props.centerLat, lng: props.centerLng }
            : DEFAULT_CENTER_COORDINATES;

    //Google maps zoom
    //If mobile the zoom are 6
    //props.googleMApsZoom (Umbraco field)
    //else if props.googleMapsZoom are undefined its zoom 7
    const googleMapsZoom = isMobile
        ? 6
        : props?.googleMapsZoom !== undefined && Number(props.googleMapsZoom) !== 0
        ? Number(props.googleMapsZoom)
        : 7;

    //activeMarker google map url
    const googleMapsUrl = `http://www.google.com/maps/dir/${props.activeMarker?.props?.googleMap?.address?.coordinates?.lat},${props.activeMarker?.props?.googleMap?.address?.coordinates?.lng}`;

    const handleActiveMarker = useCallback(
        (department: DepartmentProps) => {
            if (typeof props.onMarkerChange !== "undefined") {
                if (department.id === props.activeMarker?.id) {
                    props.onMarkerChange(undefined);
                }
                props.onMarkerChange(department);
            }
        },
        [props]
    );

    // Mappers
    const markers = useMemo(() => {
        if (isLoaded) {
            return props.departments?.map((item, index) => {
                return (
                    <div key={index} className={S.innerWrapper}>
                        {item.props?.googleMap !== null && (
                            <Marker
                                key={index}
                                onClick={() => handleActiveMarker(item)}
                                position={{
                                    lat: item.props?.googleMap?.address?.coordinates?.lat,
                                    lng: item.props?.googleMap?.address?.coordinates?.lng,
                                }}
                                icon={{
                                    url: mapPinSrc,
                                    anchor: new google.maps.Point(35 / 2, 35),
                                    labelOrigin: new google.maps.Point(35 / 2, 35),
                                }}
                            />
                        )}
                    </div>
                );
            });
        }

        return null;
    }, [isLoaded, handleActiveMarker, props.departments, mapPinSrc]);

    return isLoaded ? (
        <div className={cn(S.mapWrapper, props.className)}>
            <GoogleMap
                mapContainerClassName={S.map}
                onLoad={(map) => {
                    mapRef.current = map;
                }}
                center={CENTER_COORDINATES}
                clickableIcons={true}
                options={{
                    mapTypeControl: false,
                    zoomControl: true,
                }}
                zoom={googleMapsZoom}
            >
                <>
                    {/** Markers */}
                    {markers}

                    {props?.activeMarker ? (
                        <InfoWindow
                            position={{
                                lat: props.activeMarker?.props?.googleMap?.address?.coordinates
                                    ?.lat,
                                lng: props.activeMarker?.props?.googleMap?.address?.coordinates
                                    ?.lng,
                            }}
                            onCloseClick={() => props.onMarkerChange(undefined)}
                        >
                            <>
                                <div className={S.wrapper}>
                                    {/** City */}
                                    <DA_Title h3>
                                        {props.activeMarker.props.departmentName}
                                    </DA_Title>

                                    {/** Address */}
                                    {props.activeMarker.props.address ? (
                                        <p className={S.text}>{props.activeMarker.props.address}</p>
                                    ) : null}

                                    {/** Zip & City */}
                                    {props.activeMarker.props.zipCode ||
                                    props.activeMarker.props.city ? (
                                        <p className={S.text}>
                                            {props.activeMarker.props.zipCode}{" "}
                                            {props.activeMarker.props.city}
                                        </p>
                                    ) : null}

                                    {/** Directions */}
                                    {props.activeMarker.props.googleMap ? (
                                        <div>
                                            <DA_InlineLink
                                                title={getDictionaryString("Map.ShowRoute")}
                                                icon={DA_IconNames.Pin}
                                                href={googleMapsUrl}
                                            />
                                        </div>
                                    ) : null}

                                    {props?.activeMarker?.props.link ? (
                                        <div className={S.link}>
                                            <DA_InlineLink
                                                title={props?.activeMarker.props?.link?.name}
                                                href={props?.activeMarker.props?.link?.url}
                                                target={props?.activeMarker.props?.link?.target}
                                            />
                                        </div>
                                    ) : null}
                                </div>
                                {/** Open & Signup */}
                                {props.activeMarker.props.isHarvest ? (
                                    <div className={S.inner}>
                                        <span>{getDictionaryString("Map.HarvestOpen")}</span>
                                    </div>
                                ) : null}
                            </>
                        </InfoWindow>
                    ) : null}
                </>
            </GoogleMap>
        </div>
    ) : (
        <DA_Spinner />
    );
};

export default Map;
